<script setup lang="ts">
import type { Content } from '~/types/blog'
import { buildPostPath } from '~/composables/usePermalink'
import { toPlainExcerpt } from '~/composables/usePlainText'

const props = defineProps<{ item?: Content | null }>()
const siteData = useState<{ setting?: { customFields?: Record<string, unknown> } } | null>('site-data')
const fields = computed(() => siteData.value?.setting?.customFields)
const item = computed(() => props.item || null)
const postPath = computed(() => {
  if (!item.value) {
    return '/'
  }
  return buildPostPath(fields.value, item.value, { category: item.value.categories?.[0]?.slug || '' })
})

const excerptSource = computed(() => {
  const source = item.value?.snippet || item.value?.summary || item.value?.content || ''
  return String(source || '')
})

const excerpt = computed(() => toPlainExcerpt(excerptSource.value, 300, '暂无摘要'))

const excerptHtml = computed(() => {
  const escaped = excerptSource.value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .replace(/&lt;mark&gt;/gi, '<mark>')
    .replace(/&lt;\/mark&gt;/gi, '</mark>')
})

const dateText = computed(() => {
  if (!item.value?.publishedAt) {
    return '未发布'
  }
  return new Date(item.value.publishedAt).toLocaleDateString('zh-CN')
})
</script>

<template>
  <article v-if="item" class="card post-card">
    <div v-if="item.isTop" class="top-ribbon">
      <span><i class="fa-solid fa-thumbtack" aria-hidden="true" />置顶</span>
    </div>
    <div class="meta-row">
      <span><i class="fa-regular fa-calendar" aria-hidden="true" />{{ dateText }}</span>
      <span><i class="fa-regular fa-user" aria-hidden="true" />{{ item.authorName || '佚名' }}</span>
      <span><i class="fa-regular fa-eye" aria-hidden="true" />{{ item.viewCount || 0 }} 阅读</span>
      <span><i class="fa-regular fa-heart" aria-hidden="true" />{{ item.likeCount || 0 }} 点赞</span>
      <span><i class="fa-regular fa-comment" aria-hidden="true" />{{ item.commentCount || 0 }} 评论</span>
    </div>
    <h2>
      <NuxtLink :to="postPath">{{ item.title || '无标题' }}</NuxtLink>
    </h2>
    <p v-if="item.snippet" v-html="excerptHtml" />
    <p v-else>{{ excerpt }}</p>
    <div class="foot-row">
      <div class="chips">
        <NuxtLink
          v-for="tag in (item.tags || []).slice(0, 4)"
          :key="tag.ID"
          :to="`/tag/${encodeURIComponent(tag.slug || tag.name)}`"
          class="chip"
        >
          #{{ tag.name }}
        </NuxtLink>
      </div>
      <NuxtLink class="more" :to="postPath">阅读全文</NuxtLink>
    </div>
  </article>
</template>

<style scoped>
.post-card {
  position: relative;
  padding: 18px;
  margin-bottom: 14px;
  min-width: 0;
}

.top-ribbon {
  position: absolute;
  top: 0;
  right: 0;
  width: 106px;
  height: 106px;
  overflow: hidden;
  pointer-events: none;
}

.top-ribbon span {
  position: absolute;
  top: 18px;
  right: -28px;
  width: 132px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0;
  transform: rotate(45deg);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.4px;
  color: #fff;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--brand) 88%, #000 12%),
    color-mix(in srgb, var(--brand) 72%, #fff 28%)
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.top-ribbon i {
  font-size: 10px;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--text-soft);
  min-width: 0;
}

.meta-row span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

h2 {
  margin-bottom: 12px;
  font-size: 28px;
  min-width: 0;
}

h2 a {
  display: inline;
  color: var(--text);
  text-decoration: none;
  word-break: break-word;
  overflow-wrap: anywhere;
}

h2 a:hover {
  color: var(--brand);
}

p {
  color: var(--text-soft);
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.post-card :deep(mark) {
  padding: 0 2px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--brand) 22%, transparent);
  color: var(--text);
}

.foot-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
  min-width: 0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.chip {
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: 12px;
  color: var(--text-soft);
  text-decoration: none;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.more {
  font-weight: 700;
  flex-shrink: 0;
}

@media (max-width: 760px) {
  .top-ribbon {
    top: 0;
    right: 0;
    width: 98px;
    height: 98px;
  }

  .top-ribbon span {
    top: 16px;
    right: -30px;
    width: 124px;
  }

  h2 {
    font-size: 23px;
  }
}
</style>
